import React, { useContext } from 'react';
import type { FocusEvent } from 'react';
import { ReactReduxContext } from 'react-redux';
import { featureToggleSelectors } from '@makemydeal/dr-shared-store';
import type { StateTree } from '@makemydeal/dr-dash-types';

type SelectableInput = HTMLInputElement | HTMLTextAreaElement;
type StoreContext = { store: { getState: () => StateTree } } | null;

// Fallback used only if a test mocks react-redux without exporting ReactReduxContext.
const emptyStoreContext = React.createContext<StoreContext>(null);

/**
 * Select-all-on-focus helpers for shared inputs: clicking a field highlights its
 * value the way Tab does. The `select()` is deferred one frame so it survives the
 * click's trailing `mouseup`.
 */

export type SelectOnFocusSelector = (state: StateTree) => boolean;

/**
 * Provider-optional gate for the behavior: reads the given toggle selector from the
 * store when a react-redux Provider is present, and returns `false` otherwise.
 */
export const useSelectOnFocusEnabled = (
    selector: SelectOnFocusSelector = featureToggleSelectors.isAutoSelectContentsOnClickEnabledAfterBootstrap
): boolean => {
    /* istanbul ignore next -- emptyStoreContext fallback only hit when a test mocks react-redux without ReactReduxContext */
    const reduxContext = (ReactReduxContext ?? emptyStoreContext) as unknown as React.Context<StoreContext>;
    const context = useContext(reduxContext);
    /* istanbul ignore next -- guards for store-free hosts / partial test mocks */
    if (!context || typeof selector !== 'function') return false;
    return selector(context.store.getState());
};

const NEVER_ENABLED: SelectOnFocusSelector = () => false;

/**
 * Strict opt-in variant for host components with no prior DE753030 behavior — stays off
 * unless a selector is explicitly passed and enabled, ignoring the legacy toggle fallback
 * that `useSelectOnFocusEnabled` applies when called with no selector.
 */
export const useSelectOnFocusEnabledStrict = (selector?: SelectOnFocusSelector): boolean =>
    useSelectOnFocusEnabled(selector ?? NEVER_ENABLED);

/**
 * Composed variant for a component/field that already had DE753030 (legacy toggle)
 * select-on-focus before slide-out-specific coverage existed: stays on if EITHER the
 * legacy toggle is on, OR the given selector's toggle is on. Passing an explicit selector
 * straight to `useSelectOnFocusEnabled` bypasses its own default-to-legacy fallback, so a
 * component that must keep both toggles working can't just call it with a selector.
 */
export const useSelectOnFocusEnabledWithLegacyFallback = (selector?: SelectOnFocusSelector): boolean => {
    const isLegacyEnabled = useSelectOnFocusEnabled();
    const isSelectorEnabled = useSelectOnFocusEnabled(selector);
    return isLegacyEnabled || isSelectorEnabled;
};

let elementActiveOnWindowBlur: Element | null = null;

/* istanbul ignore next -- SSR guard */
if (typeof window !== 'undefined') {
    window.addEventListener('blur', () => {
        elementActiveOnWindowBlur = document.activeElement;
    });
    window.addEventListener('focus', () => {
        setTimeout(() => {
            elementActiveOnWindowBlur = null;
        }, 0);
    });
}

const isWindowRefocus = (el: SelectableInput): boolean => el === elementActiveOnWindowBlur;

/**
 * Select the element's contents on the next frame. Skipped when the focus event is just
 * the window regaining focus on the field the user was already editing.
 */
export const deferredSelect = (el: SelectableInput): void => {
    if (isWindowRefocus(el)) return;
    requestAnimationFrame(() => {
        if (el.isConnected) el.select();
    });
};

/**
 * Select-all for fields whose focus handler mutates the value (e.g. stripping a
 * display suffix). The Interstate input owns its value internally and syncs from
 * the `value` prop a frame late, so retry each frame until the DOM reflects the
 * expected value (frame-capped), then select.
 */
export const selectWithRetry = (el: SelectableInput, expected: string): void => {
    if (isWindowRefocus(el)) return;
    let frames = 0;
    const trySelect = () => {
        if (!el.isConnected) return;
        /* istanbul ignore next -- browser-only retry; jsdom syncs the value instantly */
        if (el.value !== expected && frames++ < 5) {
            requestAnimationFrame(trySelect);
            return;
        }
        el.select();
    };
    requestAnimationFrame(trySelect);
};

/**
 * For container-level focus delegation (ComboBox etc.) where the focus target may
 * be a button rather than the text input — select only when it's a text field.
 */
export const selectFocusedInput = (e: FocusEvent<HTMLElement>): void => {
    const el = e.target;
    if (el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement) {
        deferredSelect(el);
    }
};

export const createSelectOnFocusHandler =
    (isAutoSelectEnabled: boolean) =>
    (e: FocusEvent<HTMLInputElement | HTMLTextAreaElement>): void => {
        if (isAutoSelectEnabled) deferredSelect(e.currentTarget);
    };
